import React, { useEffect, useState } from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { apiTokensService } from "../../services/apiTokens";
import OrbitalLoader from "../common/OrbitalLoader";
import { useToast } from "../../contexts/ToastContext";

function statusClass(code) {
  if (code >= 500) return "bg-red-500/15 text-red-400";
  if (code >= 400) return "bg-amber-500/15 text-amber-400";
  if (code >= 300) return "bg-blue-500/15 text-blue-400";
  if (code >= 200) return "bg-green-500/15 text-green-500";
  return "bg-theme-secondary text-theme-text";
}

function methodClass(method) {
  const m = (method || "").toUpperCase();
  if (m === "POST") return "bg-theme-primary/15 text-theme-primary";
  if (m === "PATCH" || m === "PUT") return "bg-amber-500/15 text-amber-400";
  if (m === "DELETE") return "bg-red-500/15 text-red-400";
  return "bg-theme-secondary text-theme-text";
}

/** Flatten legacy { query, body } logs into a single top-level object. */
function flattenParams(params) {
  if (!params || typeof params !== "object" || Array.isArray(params)) return params;
  const hasLegacyNesting = Object.prototype.hasOwnProperty.call(params, "query")
    || Object.prototype.hasOwnProperty.call(params, "body");
  if (!hasLegacyNesting) return params;

  const out = {};
  if (params.query && typeof params.query === "object" && !Array.isArray(params.query)) {
    Object.assign(out, params.query);
  }
  if (params.body && typeof params.body === "object" && !Array.isArray(params.body)) {
    Object.assign(out, params.body);
  } else if (params.body != null) {
    out.body = params.body;
  }
  Object.entries(params).forEach(([key, value]) => {
    if (key !== "query" && key !== "body") out[key] = value;
  });
  return out;
}

const ApiRequestDetail = ({ hash }) => {
  const { showToast } = useToast();
  const [row, setRow] = useState(null);

  useEffect(() => {
    if (!hash) return;
    apiTokensService.getRequest(hash, (res) => setRow(res.data), () => setRow(null));
  }, [hash]);

  const copyResponse = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(row?.response ?? null, null, 2));
      showToast("Response copied");
    } catch (_) {
      showToast("Could not copy response", { type: "error" });
    }
  };

  if (!row) {
    return (
      <div className="flex items-center justify-center h-full min-h-[16rem] p-6">
        <OrbitalLoader size={64} />
      </div>
    );
  }

  const requestParams = flattenParams(row.params);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 px-6 pt-5 pb-4 border-b border-theme-secondary">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${methodClass(row.method)}`}>
              {row.method}
            </span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusClass(row.status_code)}`}>
              {row.status_code}
            </span>
            <span className="text-xs text-theme-text opacity-60">{row.milliseconds}ms</span>
          </div>
          <p className="text-xs text-theme-text opacity-60 shrink-0">
            {new Date(row.create_date).toLocaleString()}
            {row.token_name ? ` · ${row.token_name}` : ""}
          </p>
        </div>
        <h2 className="text-base font-semibold text-theme-text font-mono break-all mt-2">
          {row.endpoint}
        </h2>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
        <JsonBlock title="Request" value={requestParams} />
        <JsonBlock title="Response" value={row.response} onCopy={copyResponse} tall />
      </div>
    </div>
  );
};

function JsonBlock({ title, value, onCopy, tall = false }) {
  return (
    <div className="rounded-lg border border-theme-secondary overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-theme-secondary bg-theme-secondary bg-opacity-30">
        <h3 className="text-sm font-semibold text-theme-text">{title}</h3>
        {onCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="inline-flex items-center gap-1.5 text-xs text-theme-primary hover:opacity-80"
            title={`Copy ${title.toLowerCase()}`}
          >
            <ClipboardDocumentIcon className="h-4 w-4" />
            Copy
          </button>
        )}
      </div>
      <pre
        className={`text-xs font-mono whitespace-pre-wrap break-all bg-theme-main p-3 text-theme-text ${
          tall ? "max-h-[46vh] overflow-auto" : "max-h-[24vh] overflow-auto"
        }`}
      >
        {JSON.stringify(value ?? null, null, 2)}
      </pre>
    </div>
  );
}

export default ApiRequestDetail;
